import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "semantic-ui-css/semantic.min.css";
import "../OutboundCampaign/OutboundCampaign.css";
import api from "../../../api";
import DailyWeeklyScheduleFields from "../OutboundCampaign/CampaignScheduleFields/DailyWeeklyScheduleFields";
import RadioOptionGroupSMS from "./components/RadioOptionGroupSMS";
import CheckboxOptionGroupSMS from "./components/CheckboxOptionGroupSMS";
import WeekdaySelectorSMS from "./components/WeekdaySelectorSMS";
import { formatDateForInput, isDateInPast } from "../OutboundCampaign/scheduleUtils";
import { Dropdown, Icon, Message } from "semantic-ui-react";
import constants from "../../../util_funs/constant";
import CustomersUploadAndMappingSMS from "./customers/CustomersUploadAndMappingSMS";
import CustomersTablePanelSMS from "./customers/CustomersTablePanelSMS";
import { convertBackendErrors, scrollToFirstError } from "../../../util_funs";
import ConfirmationModal from "../Workspace/Botbuilder/Details/confusedbranch/DataConnect/ConfirmationModal";
import SequenceCampaignSection, {
  MAX_SEQUENCE_STEPS,
  hasNonPendingSequenceSteps,
  normalizeSequenceSteps,
} from "./components/SequenceCampaignSection";

const CATEGORY_OPTIONS = [
  { key: "general_non_marketing", value: "non_marketing", text: "General Non-Marketing" },
  { key: "marketing", value: "marketing", text: "Marketing" },
];

function coerceInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
}

function getCurrentTimeHHMM() {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function getCurrentTimePlusHoursHHMM(hoursToAdd = 1) {
  const now = new Date();
  now.setHours(now.getHours() + hoursToAdd);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function normalizeTimeToHHMM(value) {
  if (!value) return "";

  const raw = String(value).trim();
  const amPmMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)$/i);
  if (amPmMatch) {
    let hours = parseInt(amPmMatch[1], 10);
    const minutes = amPmMatch[2];
    const modifier = amPmMatch[3].toUpperCase();

    if (modifier === "AM" && hours === 12) hours = 0;
    if (modifier === "PM" && hours !== 12) hours += 12;

    return `${String(hours).padStart(2, "0")}:${minutes}`;
  }

  const twentyFourHourMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFourHourMatch) {
    return `${twentyFourHourMatch[1].padStart(2, "0")}:${twentyFourHourMatch[2]}`;
  }

  return raw;
}

function getTodayYYYYMMDD() {
  // HTML date input expects YYYY-MM-DD
  return new Date().toLocaleDateString("en-CA");
}

function formatStartDateForInput(dateStr) {
  if (!dateStr) return "";
  const raw = String(dateStr);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return formatDateForInput(raw);
}

function normalizeDaysOfWeek(days) {
  if (Array.isArray(days)) return days;
  if (typeof days === "string") {
    return days
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);
  }
  return [];
}

function mapSmsCampaignToFormData(campaign) {
  const schedule = campaign?.schedule || {};
  return {
    outbound_twilio_number_id: String(
      campaign.outbound_twilio_number_id || campaign.outbound_number?.id || ""
    ),
    name: campaign.name || "",
    workspace_id: String(campaign.workspace_id || ""),
    campaign_category: campaign.campaign_category || "",
    consent_type: campaign.consent_type || "",
    consent_notes: campaign.consent_notes || "",
    type: campaign.type || "manual",
    webhook_url: campaign.webhook_url || "",
    send_type: campaign.send_type || "now",
    schedule_type: schedule.schedule_type || "one_time",
    days_of_week: normalizeDaysOfWeek(schedule.days || schedule.days_of_week),
    start_time: normalizeTimeToHHMM(schedule.start_time),
    end_time: normalizeTimeToHHMM(schedule.end_time),
    start_date: formatStartDateForInput(schedule.start_date),
    timezone:
      schedule.timezone ||
      campaign.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    rate_limit_per_min: campaign.rate_limit_per_min ?? 5,
    max_turns: campaign.max_turns ?? 10,
    conversation_timeout: campaign.conversation_timeout ?? 8,
    is_sequence_campaign:
      campaign.is_sequence_campaign === true ||
      campaign.is_sequence_campaign === 1,
    sequences: normalizeSequenceSteps(campaign.sequences),
    campaign_status: campaign.status || "",
    inbound_voice_enabled: !!campaign.voice_sources?.inbound_enabled,
    outbound_voice_enabled: Array.isArray(campaign.voice_sources?.outbound_voice_campaigns)
      ? campaign.voice_sources.outbound_voice_campaigns.length > 0
      : false,
    outbound_voice_campaign_ids: Array.isArray(campaign.voice_sources?.outbound_voice_campaigns)
      ? campaign.voice_sources.outbound_voice_campaigns.map((c) => c.id)
      : [],
  };
}

const CampaignSMSForm = ({
  user_id,
  onSave,
  onCancel,
  compactMode = false,
  campaign_id,
  editingCampaign,
}) => {
  const isEditing = !!(campaign_id || editingCampaign?.id);
  const resolvedCampaignId = campaign_id || editingCampaign?.id;

  const [loading, setLoading] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showSendNowConfirmModal, setShowSendNowConfirmModal] = useState(false);
  const errorMessageRef = useRef(null);

  const [activeWorkspaces, setActiveWorkspaces] = useState([]);
  const [availableOutboundNumbers, setAvailableOutboundNumbers] = useState([]);
  const [outboundNumbersLoading, setOutboundNumbersLoading] = useState(false);
  const [outboundNumbersMessage, setOutboundNumbersMessage] = useState("");
  const [formData, setFormData] = useState({
    outbound_twilio_number_id: "",
    name: "",
    workspace_id: "",
    campaign_category: "",
    consent_type: "",
    consent_notes: "",
    type: "manual", // manual | triggered
    send_type: "now", // now | scheduled
    schedule_type: "one_time", // one_time | ongoing
    days_of_week: [],
    start_time: "",
    end_time: "",
    start_date: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    rate_limit_per_min: 5,
    max_turns: 10,
    conversation_timeout: 8,
    is_sequence_campaign: false,
    sequences: [],
    campaign_status: "",
    inbound_voice_enabled: false,
    outbound_voice_enabled: false,
    outbound_voice_campaign_ids: [],
  });

  const [voiceCampaignOptions, setVoiceCampaignOptions] = useState([]);
  const [voiceCampaignsLoading, setVoiceCampaignsLoading] = useState(false);

  const [customersConfig, setCustomersConfig] = useState({
    contact_count: 0,
    invalid_rows_skipped: 0,
    duplicates_found: 0,
    dedupe_mode: "dedupe",
    variable_map: {},
    customers: [],
    preview_messages: [],
    warnings: [],
  });

  const isTriggered = formData.type === "triggered";
  const isScheduled = formData.send_type === "scheduled";
  // Until dispatcher ships, API won't return non-pending status.
  // Don't freeze on campaign status; only lock when a step is known non-pending.
  // (Matches BE: sequence config editable on processing/paused/stopped while all steps pending.)
  const sequenceLocked = hasNonPendingSequenceSteps(formData.sequences);

  const inputStyle = useMemo(
    () => ({
      borderRadius: "5px",
      padding: "10px 15px",
      fontSize: "14px",
    }),
    []
  );

  // Triggered campaigns cannot use Send Now.
  useEffect(() => {
    if (!isTriggered) return;
    if (formData.send_type !== "scheduled") {
      setFormData((p) => ({ ...p, send_type: "scheduled" }));
    }
  }, [isTriggered, formData.send_type]);

  // Match OutboundCampaign defaults: when Scheduled is selected, auto-populate times/date.
  useEffect(() => {
    if (!isScheduled) return;
    setFormData((p) => {
      const next = { ...p };
      if (!next.start_time) next.start_time = getCurrentTimeHHMM();
      if (!next.end_time) next.end_time = getCurrentTimePlusHoursHHMM(1);
      if (!next.start_date) next.start_date = getTodayYYYYMMDD();
      return next;
    });
  }, [isScheduled]);

  const workspaceOptions = useMemo(() => {
    const list = Array.isArray(activeWorkspaces) ? activeWorkspaces : [];
    return list.map((w) => ({
      key: w.id,
      value: w.id,
      text: w.name ?? "Workspace",
    }));
  }, [activeWorkspaces]);

  const selectedWorkspace = useMemo(() => {
    const id = String(formData.workspace_id || "");
    if (!id) return null;
    return (Array.isArray(activeWorkspaces) ? activeWorkspaces : []).find(
      (w) => String(w.id) === id
    );
  }, [activeWorkspaces, formData.workspace_id]);

  const handleInputChange = (key, value) => {
    setFormData((p) => ({ ...p, [key]: value }));
    setFormErrors((p) => {
      if (!p[key]) return p;
      const { [key]: _, ...rest } = p;
      return rest;
    });
  };

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      const res = await api.outbound.getActiveOutboundWorkspaces({
        user_id,
        type: "OUTBOUND_SMS",
      });
      if (res?.status && Array.isArray(res.data)) {
        setActiveWorkspaces(res.data);
      } else {
        setActiveWorkspaces([]);
      }
    } catch (e) {
      setActiveWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableOutboundNumbers = async () => {
    try {
      setOutboundNumbersLoading(true);
      const res = await api.outbound.getOutboundSmsPhoneNumbers({
        user_id,
        type: "outbound",
      });
      if (res?.status) {
        const numbers = Array.isArray(res.data) ? res.data : [];
        setAvailableOutboundNumbers(numbers);
        setOutboundNumbersMessage(
          numbers.length ? "" : "No outbound SMS numbers are configured. Please contact support."
        );
      } else {
        setAvailableOutboundNumbers([]);
        setOutboundNumbersMessage(res?.message || "Unable to load outbound SMS numbers.");
      }
    } catch (e) {
      setAvailableOutboundNumbers([]);
      setOutboundNumbersMessage("Unable to load outbound SMS numbers.");
    } finally {
      setOutboundNumbersLoading(false);
    }
  };

  const loadVoiceCampaignOptions = async () => {
    try {
      setVoiceCampaignsLoading(true);
      const res = await api.outbound.CampaignsList({ user_id, limit: 200 });
      const campaigns = res?.data?.campaigns;
      setVoiceCampaignOptions(
        Array.isArray(campaigns)
          ? campaigns.map((c) => ({ key: c.id, value: c.id, text: c.name }))
          : []
      );
    } catch (e) {
      setVoiceCampaignOptions([]);
    } finally {
      setVoiceCampaignsLoading(false);
    }
  };

  const loadDefaultTimezone = async () => {
    if (isEditing) return;
    try {
      const res = await api.outbound.getAccountDefaultTimezone({ user_id });
      const tz = res?.data?.timezone;
      if (tz) {
        setFormData((p) => ({ ...p, timezone: tz }));
      }
    } catch (_) {
      // keep browser tz
    }
  };

  const buildSnapshot = useCallback(
    (data, customers) =>
      JSON.stringify({
        form: data,
        customers: Array.isArray(customers) ? customers : [],
      }),
    []
  );

  const hasUnsavedChanges = useCallback(() => {
    if (isEditing) {
      if (!initialSnapshot) return false;
      return (
        buildSnapshot(formData, customersConfig.customers) !== initialSnapshot
      );
    }
    return !!(
      formData.name?.trim() ||
      formData.workspace_id ||
      formData.campaign_category ||
      formData.consent_type ||
      (customersConfig.customers && customersConfig.customers.length > 0)
    );
  }, [isEditing, initialSnapshot, formData, customersConfig.customers, buildSnapshot]);

  const applyCampaignToForm = useCallback(
    (campaign) => {
      const mapped = mapSmsCampaignToFormData(campaign);
      setFormData(mapped);
      const customers = Array.isArray(campaign.customers) ? campaign.customers : [];
      setCustomersConfig((prev) => ({
        ...(prev || {}),
        customers,
        contact_count: customers.length,
      }));
      setInitialSnapshot(buildSnapshot(mapped, customers));
    },
    [buildSnapshot]
  );

  const loadCampaignDetails = async () => {
    if (!resolvedCampaignId) return;
    try {
      setCampaignLoading(true);
      const tz =
        editingCampaign?.schedule?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await api.outbound.getSmsCampaign(resolvedCampaignId, tz);
      if (res?.status && res.data) {
        applyCampaignToForm(res.data);
      } else if (editingCampaign) {
        applyCampaignToForm(editingCampaign);
        setMessage({
          type: "error",
          text: res?.message || "Unable to load campaign details.",
        });
      }
    } catch (_) {
      if (editingCampaign) {
        applyCampaignToForm(editingCampaign);
      }
    } finally {
      setCampaignLoading(false);
    }
  };

  useEffect(() => {
    if (!user_id) return;
    loadWorkspaces();
    loadAvailableOutboundNumbers();
    loadDefaultTimezone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  // Voice campaign picker only matters once "Triggered" + "Outbound Voice Campaign" is in play.
  useEffect(() => {
    if (!user_id || !isTriggered || voiceCampaignOptions.length || voiceCampaignsLoading) return;
    loadVoiceCampaignOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id, isTriggered]);

  useEffect(() => {
    if (!isEditing) return;
    if (editingCampaign) {
      applyCampaignToForm(editingCampaign);
    }
    loadCampaignDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedCampaignId]);

  const validate = () => {
    const errors = {};
    if (!formData.outbound_twilio_number_id) {
      errors.outbound_twilio_number_id = "Outbound SMS number is required.";
    }
    if (!formData.name?.trim()) errors.name = "Campaign name is required.";
    if (!formData.workspace_id) errors.workspace_id = "Workspace is required.";
    if (!formData.campaign_category) errors.campaign_category = "Campaign category is required.";
    if (!formData.consent_type) {
      errors.consent_type = "Please select a Consent Type.";
    } else if (formData.consent_type === "other") {
      if (!formData.consent_notes || String(formData.consent_notes).trim().length < 10) {
        errors.consent_notes = "Please describe how consent was obtained (min 10 characters).";
      }
    }

    if (formData.type === "triggered" && formData.outbound_voice_enabled && formData.outbound_voice_campaign_ids.length === 0) {
      errors.voice_sources = "Select at least one outbound voice campaign, or uncheck this source.";
    }

    // Customers upload (CSV + mapping) — required for non-triggered campaigns
    if (formData.type !== "triggered") {
      if (!customersConfig?.customers || customersConfig.customers.length === 0) {
        errors.customers = "At least one customer is required to save the campaign.";
      }
      if (Array.isArray(customersConfig?.warnings) && customersConfig.warnings.length > 0) {
        errors.customers = "Please map all workspace variables in the CSV before proceeding.";
      }
    }

    if (isScheduled) {
      const { schedule_type, start_date, start_time, end_time, timezone } = formData;

      if (!schedule_type) {
        errors.schedule_type = "Schedule type is required.";
      } else if (schedule_type === "ongoing" || schedule_type === "one_time") {
        if (!start_date) errors.start_date = "Start date is required.";
        if (!start_time) errors.start_time = "Start time is required.";
        if (!end_time) errors.end_time = "End time is required.";
        if (!timezone) errors.timezone = "Timezone is required.";

        if (start_date && start_time && timezone && !isEditing) {
          if (isDateInPast(start_date, timezone)) {
            errors.start_date = "Start date should not be in the past.";
          }
          // if (isDateTimeInPast(start_date, start_time, timezone)) {
          //   errors.start_time = "Start time should not be in the past.";
          // }
        }
      }
    }

    const rateLimitPerMin = coerceInt(formData.rate_limit_per_min, 5);
    if (rateLimitPerMin < 0 || rateLimitPerMin > 1000) {
      errors.rate_limit_per_min = "Send rate limit must be 0 to 1000.";
    }
    const maxConversationTurns = coerceInt(formData.max_turns, NaN);
    if (
      formData.max_turns === "" ||
      formData.max_turns === null ||
      formData.max_turns === undefined ||
      Number.isNaN(maxConversationTurns)
    ) {
      errors.max_turns = "Max conversation turns is required.";
    } else if (maxConversationTurns < 1) {
      errors.max_turns = "Max turns cannot be less than 1.";
    } else if (maxConversationTurns > 20) {
      errors.max_turns = "Max turns must be 1 to 20.";
    }
    const conversationTimeoutHours = coerceInt(formData.conversation_timeout, NaN);
    if (
      formData.conversation_timeout === "" ||
      formData.conversation_timeout === null ||
      formData.conversation_timeout === undefined ||
      Number.isNaN(conversationTimeoutHours)
    ) {
      errors.conversation_timeout = "Conversation timeout is required.";
    } else if (conversationTimeoutHours < 1) {
      errors.conversation_timeout = "Conversation timeout cannot be less than 1 hour.";
    } else if (conversationTimeoutHours > 168) {
      errors.conversation_timeout = "Conversation timeout cannot be more than 168 hours (7 days).";
    }

    if (formData.is_sequence_campaign && !sequenceLocked) {
      const sequences = Array.isArray(formData.sequences) ? formData.sequences : [];
      if (sequences.length === 0) {
        errors.sequences = "Add at least one sequence step.";
      } else if (sequences.length > MAX_SEQUENCE_STEPS) {
        errors.sequences = `You can add up to ${MAX_SEQUENCE_STEPS} sequences.`;
      } else {
        sequences.forEach((step, index) => {
          if (!step?.workspace_id) {
            errors[`sequences.${index}.workspace_id`] =
              "Workspace is required for this sequence.";
          }
          const days = coerceInt(step?.days_until_send, NaN);
          if (Number.isNaN(days) || days < 1 || days > 365) {
            errors[`sequences.${index}.days_until_send`] =
              "Days must be between 1 and 365.";
          }
        });
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (saving) return;
    setMessage(null);
    setFormErrors({});

    const errors = validate();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      setTimeout(() => {
        // Show top banner only when the ONLY validation error is customers
        if (errors.customers && Object.keys(errors).length === 1) {
          setMessage({ type: "error", text: errors.customers });
          setTimeout(() => setMessage(null), 7000);
        } else {
          setMessage(null);
        }
        scrollToFirstError(errors);
        if (errorMessageRef.current) {
          errorMessageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);
      return;
    }

    // A new Send Now campaign can start texting as soon as it is saved, so ask first.
    if (!isEditing && !isTriggered && formData.send_type === "now") {
      setShowSendNowConfirmModal(true);
      return;
    }

    await saveCampaign();
  };

  const confirmSendNow = () => {
    setShowSendNowConfirmModal(false);
    saveCampaign();
  };

  const saveCampaign = async () => {
    setSaving(true);
    try {
      const payload = {
        outbound_twilio_number_id: parseInt(formData.outbound_twilio_number_id, 10),
        name: formData.name.trim(),
        workspace_id: parseInt(formData.workspace_id, 10),
        campaign_category: formData.campaign_category,
        consent_type: formData.consent_type,
        consent_notes: formData.consent_type === "other" ? formData.consent_notes : "",
        type: formData.type,
        send_type: formData.send_type,
        schedule_type: formData.send_type === "now" ? "" : formData.schedule_type,
        days_of_week: formData.days_of_week,
        start_time: formData.send_type === "now" ? "" : normalizeTimeToHHMM(formData.start_time),
        end_time: formData.send_type === "now" ? "" : normalizeTimeToHHMM(formData.end_time),
        start_date: formData.send_type === "now" ? "" : formData.start_date,
        timezone: formData.send_type === "now" ? "" : formData.timezone,
        rate_limit_per_min: coerceInt(formData.rate_limit_per_min, 5),
        max_turns: coerceInt(formData.max_turns, 10),
        conversation_timeout: coerceInt(formData.conversation_timeout, 8),
        campaign_channel: "sms",
        // Omit sequence keys when locked so other field updates don't 422 on backend.
        ...(!sequenceLocked
          ? {
              is_sequence_campaign: !!formData.is_sequence_campaign,
              sequences: formData.is_sequence_campaign
                ? (Array.isArray(formData.sequences) ? formData.sequences : []).map(
                    (step, index) => ({
                      ...(step.id ? { id: step.id } : {}),
                      sequence_number: index + 1,
                      workspace_id: parseInt(step.workspace_id, 10),
                      days_until_send: coerceInt(step.days_until_send, NaN),
                    })
                  )
                : [],
            }
          : {}),
        // Customers CSV flow (mirrors voice `customers`, but tailored for SMS personalisation)
        ...(formData.type !== "triggered"
          ? {
            customers: customersConfig.customers,
            variable_map: customersConfig.variable_map,
            contact_count: customersConfig.contact_count,
            dedupe_mode: customersConfig.dedupe_mode,
          }
          : {}),
      };

      // Voice sources go through a separate endpoint (sms-campaigns/{id}/voice-sources), not the
      // main campaign payload -- kept as a sibling key here so the parent (which owns the
      // store/update call and therefore knows the created/updated campaign id) can save it as a
      // decoupled follow-up call after the campaign itself is saved.
      if (isTriggered) {
        payload.voice_sources = {
          inbound_enabled: !!formData.inbound_voice_enabled,
          outbound_voice_campaign_ids: formData.outbound_voice_enabled
            ? formData.outbound_voice_campaign_ids.map((id) => parseInt(id, 10))
            : [],
        };
      }

      const res = await onSave?.(payload);
      if (res && res.status === false) {
        // Voice-like behavior: show API errors on top; map validation errors onto fields and scroll.
        const backendErrors = res?.errors && typeof res.errors === "object" ? convertBackendErrors(res.errors) : null;
        if (backendErrors && Object.keys(backendErrors).length > 0) {
          setFormErrors((prev) => ({ ...(prev || {}), ...backendErrors }));
          setTimeout(() => scrollToFirstError(backendErrors), 50);
          return;
        }

        setMessage({ type: "error", text: res.message || "Failed to save campaign." });
        return;
      }

      // If backend returns webhook URL for triggered, show it once.
      const webhook =
        res?.data?.campaign?.webhook_url ||
        res?.data?.webhook_url ||
        res?.campaign?.webhook_url ||
        res?.webhook_url;
      if (isTriggered && webhook) {
        setMessage({ type: "success", text: `Webhook generated: ${webhook}` });
      }
    } catch (e) {
      setMessage({ type: "error", text: e?.message || "Failed to save campaign." });
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges()) {
      setShowCancelConfirmModal(true);
      return;
    }
    if (onCancel) onCancel();
  };

  const confirmDiscardChanges = () => {
    setShowCancelConfirmModal(false);
    if (onCancel) onCancel();
  };

  if (campaignLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
          width: "100%",
        }}
      >
        <div className="ui active inline loader" />
      </div>
    );
  }

  return (
    <div
      className={compactMode ? "" : "yr-chat-round-box addEditBranch"}
      style={{
        ...(compactMode && { border: "none", boxShadow: "none", borderRadius: 0, padding: 0, margin: 0 }),
      }}
    >
      <div className="row">
        <div className="col-md-6 text-left">
          <button
            className="linear-gradient yr-submitbtn btn-sm"
            onClick={handleBackToList}
            style={{
              border: "1px solid #6c757d",
              color: "#6c757d",
              borderRadius: "5px",
              padding: "8px 16px",
              fontSize: "14px",
            }}
          >
            <i className="la la-arrow-left mr-2"></i>
            Back To List
          </button>
        </div>
      </div>

      <div className="row mt-4">
        <div className="add-branch-title mb-3 col-md-6">
          {isEditing ? "Edit SMS Campaign" : "Add SMS Campaign"}
        </div>
      </div>

      {message && (
        <div ref={errorMessageRef}>
          <Message
            className="campaign-page-message"
            color={message.type === "success" ? "green" : message.type === "info" ? "blue" : "red"}
            style={{ display: message ? "block" : "none", marginTop: 30 }}
          >
            {message?.text}
          </Message>
        </div>
      )}

      <div className="row">
        <div className="col-md-7">
          <div className="row">
            <div className="col-md-6">
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="pd10botbuilder">
                  Outbound Number<span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="outbound_twilio_number_id"
                  className={`form-control ${formErrors.outbound_twilio_number_id ? "is-invalid" : ""}`}
                  value={formData.outbound_twilio_number_id || ""}
                  onChange={(e) => handleInputChange("outbound_twilio_number_id", e.target.value)}
                  disabled={outboundNumbersLoading}
                  style={{
                    border: formErrors.outbound_twilio_number_id ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                >
                  <option value="">
                    {outboundNumbersLoading ? "Loading outbound numbers..." : "Select Outbound Number"}
                  </option>
                  {availableOutboundNumbers.map((n) => (
                    <option key={n.id} value={n.id}>
                      {(n.phone_number)}
                    </option>
                  ))}
                </select>
                {outboundNumbersMessage && availableOutboundNumbers.length === 0 && (
                  <div className="text-muted" style={{ fontSize: "12px", marginTop: 5 }}>
                    {outboundNumbersMessage}
                  </div>
                )}
                {formErrors.outbound_twilio_number_id && (
                  <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.outbound_twilio_number_id}</div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label className="pd10botbuilder">
                  Campaign Name<span style={{ color: "red" }}>*</span>
                </label>
                <input
                  name="name"
                  className={`form-control ${formErrors.name ? "is-invalid" : ""}`}
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter campaign name"
                  style={{
                    border: formErrors.name ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                />
                {formErrors.name && <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.name}</div>}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label className="pd10botbuilder">
                  Workspace<span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="workspace_id"
                  className={`form-control ${formErrors.workspace_id ? "is-invalid" : ""}`}
                  value={formData.workspace_id}
                  onChange={(e) => handleInputChange("workspace_id", e.target.value)}
                  disabled={loading}
                  style={{
                    border: formErrors.workspace_id ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                >
                  <option value="">{loading ? "Loading..." : "Select Workspace"}</option>
                  {workspaceOptions.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.text}
                    </option>
                  ))}
                </select>
                {formErrors.workspace_id && <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.workspace_id}</div>}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="form-group">
                <label className="pd10botbuilder">
                  Campaign Category<span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="campaign_category"
                  className={`form-control ${formErrors.campaign_category ? "is-invalid" : ""}`}
                  value={formData.campaign_category}
                  onChange={(e) => handleInputChange("campaign_category", e.target.value)}
                  style={{
                    border: formErrors.campaign_category ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                >
                  <option value="">Select Campaign Category</option>
                  {CATEGORY_OPTIONS.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.text}
                    </option>
                  ))}
                </select>
                {formErrors.campaign_category && (
                  <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                    {formErrors.campaign_category}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="pd10botbuilder">
                  Consent Type<span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="consent_type"
                  className={`form-control ${formErrors.consent_type ? "is-invalid" : ""}`}
                  value={formData.consent_type || ""}
                  onChange={(e) => handleInputChange("consent_type", e.target.value)}
                  style={{
                    border: formErrors.consent_type ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                >
                  <option value="">Select Consent Type</option>
                  {constants.CONSENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.text}
                    </option>
                  ))}
                </select>
                {formErrors.consent_type && (
                  <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                    {formErrors.consent_type}
                  </div>
                )}
              </div>

              {formData.consent_type === "other" && (
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="pd10botbuilder">
                    Other (Describe)<span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    name="consent_notes"
                    className={`form-control ${formErrors.consent_notes ? "is-invalid" : ""}`}
                    value={formData.consent_notes || ""}
                    onChange={(e) => handleInputChange("consent_notes", e.target.value)}
                    placeholder="Describe how consent was obtained"
                    minLength={10}
                    maxLength={500}
                    style={{
                      border: formErrors.consent_notes ? "1px solid #dc3545" : "1px solid #e1e5e9",
                      ...inputStyle,
                      resize: "vertical",
                      minHeight: 100,
                      maxHeight: 150,
                    }}
                  />
                  <div style={{ fontSize: "12px", color: "#6c757d", marginTop: 4 }}>
                    {(formData.consent_notes?.length || 0)} / 500 characters
                  </div>
                  {formErrors.consent_notes && (
                    <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                      {formErrors.consent_notes}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Campaign Type */}
          <RadioOptionGroupSMS
            label="Campaign Type"
            name="sms_campaignType"
            selectedValue={formData.type}
            onChange={(value) => handleInputChange("type", value)}
            options={[
              {
                value: "manual",
                label: "Manual",
                description: "Start campaign now or schedule for later.",
              },
              {
                value: "triggered",
                label: "Triggered",
                description: "Triggered via webhooks from your website or forms.",
              },
            ]}
          />

          {isTriggered && (
            <div className="row mt-2">
              <div className="col-md-12">
                <Message info>
                  <Icon name="info circle" />
                  {formData.webhook_url
                    ? `Webhook URL: ${formData.webhook_url}`
                    : "A unique webhook will be generated to connect your data source."}
                </Message>
              </div>
            </div>
          )}

          {isTriggered && (
            <div data-field="voice_sources" tabIndex={-1}>
              <CheckboxOptionGroupSMS
                label="Choose Call Data Source"
                name="voice_data_source"
                selectedValues={[
                  ...(formData.inbound_voice_enabled ? ["inbound"] : []),
                  ...(formData.outbound_voice_enabled || formData.outbound_voice_campaign_ids.length > 0 ? ["outbound"] : []),
                ]}
                onChange={(values) => {
                  handleInputChange("inbound_voice_enabled", values.includes("inbound"));
                  const outboundChecked = values.includes("outbound");
                  handleInputChange("outbound_voice_enabled", outboundChecked);
                  if (!outboundChecked) handleInputChange("outbound_voice_campaign_ids", []);
                }}
                options={[
                  {
                    value: "inbound",
                    label: "Inbound Calls",
                    description: "Automatically manage missed and abandoned incoming calls.",
                    disabled: saving,
                  },
                  {
                    value: "outbound",
                    label: "Outbound Calls",
                    description: "Automatically manage missed and abandoned calls from your outbound voice campaigns.",
                    disabled: saving,
                  },
                ]}
              />

              {(formData.outbound_voice_enabled || formData.outbound_voice_campaign_ids.length > 0) && (
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label className="pd10botbuilder">Outbound Voice Campaign(s)</label>
                      <Dropdown
                        placeholder="Select outbound voice campaign(s)"
                        fluid
                        selection
                        multiple
                        search
                        loading={voiceCampaignsLoading}
                        options={voiceCampaignOptions}
                        value={formData.outbound_voice_campaign_ids}
                        onChange={(e, { value }) => handleInputChange("outbound_voice_campaign_ids", value)}
                        disabled={saving}
                        style={{
                          border: formErrors.voice_sources ? "1px solid #dc3545" : "1px solid #e1e5e9",
                          ...inputStyle,
                        }}
                      />
                      {formErrors.voice_sources && (
                        <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                          {formErrors.voice_sources}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Customers actions (table renders on right column like Voice) */}
          {!isTriggered && (
            <div className="row" style={{ marginTop: -5 }}>
              <div className="col-md-12">
                <div data-field="customers" tabIndex={-1}>
                  <CustomersUploadAndMappingSMS
                    workspace={selectedWorkspace}
                    workspaceIntroMessage={
                      selectedWorkspace?.intro_message ??
                      selectedWorkspace?.intro ??
                      selectedWorkspace?.description ??
                      selectedWorkspace?.welcome_message ??
                      selectedWorkspace?.message ??
                      ""
                    }
                    customers={customersConfig.customers}
                    onCustomersChange={(updater) => {
                      setCustomersConfig((prev) => {
                        const nextCustomers =
                          typeof updater === "function" ? updater(prev.customers || []) : updater;
                        return {
                          ...(prev || {}),
                          customers: Array.isArray(nextCustomers) ? nextCustomers : [],
                          contact_count: Array.isArray(nextCustomers) ? nextCustomers.length : 0,
                        };
                      });
                    }}
                    disabled={saving}
                    onChange={(next) => {
                      setCustomersConfig(next || {});
                      setFormErrors((p) => {
                        if (!p.customers) return p;
                        const { customers: _, ...rest } = p;
                        return rest;
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Send Type */}
          <RadioOptionGroupSMS
            label="Choose when to send"
            name="sms_sendType"
            selectedValue={formData.send_type}
            onChange={(value) => handleInputChange("send_type", value)}
            columns={6}
            options={[
              {
                value: "now",
                label: "Send Now",
                description: isTriggered ? "Disabled for triggered campaigns." : "Send the campaign instantly.",
                disabled: isTriggered,
              },
              {
                value: "scheduled",
                label: "Scheduled",
                description: "Send at specific time.",
              },
            ]}
          />

          {isScheduled && (
            <>
              <div className="row mt-2">
                <div className="col-md-12">
                  <RadioOptionGroupSMS
                    label="Schedule Type"
                    name="sms_schedule_type"
                    selectedValue={formData.schedule_type}
                    onChange={(val) => handleInputChange("schedule_type", val)}
                    columns={6}
                    options={[
                      { value: "one_time", label: "One Time", description: "Send once at scheduled time." },
                      { value: "ongoing", label: "Ongoing", description: "The campaign stays active as new data is added." },
                    ]}
                  />
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-md-12">
                  <WeekdaySelectorSMS
                    selectedDays={formData.days_of_week}
                    onChange={(value) => handleInputChange("days_of_week", value)}
                    error={formErrors.days_of_week}
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-12">
                  <DailyWeeklyScheduleFields
                    formData={formData}
                    formErrors={formErrors}
                    handleInputChange={handleInputChange}
                    canEditField={() => true}
                    isEditing={isEditing}
                    setLoading={setLoading}
                  />
                </div>
              </div>
            </>
          )}

          <div className="row mt-3">
            <div className="col-md-6">
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="pd10botbuilder">Send rate limit (messages/min)</label>
                <input
                  type="number"
                  min={0}
                  max={1000}
                  className={`form-control ${formErrors.rate_limit_per_min ? "is-invalid" : ""}`}
                  value={formData.rate_limit_per_min}
                  onChange={(e) => handleInputChange("rate_limit_per_min", e.target.value)}
                  style={{
                    border: formErrors.rate_limit_per_min ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                />
                {formErrors.rate_limit_per_min && (
                  <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                    {formErrors.rate_limit_per_min}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="pd10botbuilder">
                  Max conversation turns<span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  className={`form-control ${formErrors.max_turns ? "is-invalid" : ""}`}
                  value={formData.max_turns}
                  onChange={(e) => handleInputChange("max_turns", e.target.value)}
                  style={{
                    border: formErrors.max_turns ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                />
                {formErrors.max_turns && (
                  <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                    {formErrors.max_turns}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="pd10botbuilder">
                  Conversation timeout (hours)<span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={999}
                  className={`form-control ${formErrors.conversation_timeout ? "is-invalid" : ""}`}
                  value={formData.conversation_timeout}
                  onChange={(e) => handleInputChange("conversation_timeout", e.target.value)}
                  style={{
                    border: formErrors.conversation_timeout ? "1px solid #dc3545" : "1px solid #e1e5e9",
                    ...inputStyle,
                  }}
                />
                {formErrors.conversation_timeout && (
                  <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                    {formErrors.conversation_timeout}
                  </div>
                )}
              </div>
            </div>
          </div>

          <SequenceCampaignSection
            enabled={!!formData.is_sequence_campaign}
            sequences={formData.sequences}
            workspaceOptions={workspaceOptions}
            errors={formErrors}
            disabled={saving || sequenceLocked}
            inputStyle={inputStyle}
            onNotify={(text) => {
              setMessage({ type: "error", text });
              setTimeout(() => setMessage(null), 7000);
            }}
            onToggle={(enabled) => {
              setFormData((p) => ({
                ...p,
                is_sequence_campaign: enabled,
                sequences: enabled
                  ? Array.isArray(p.sequences) && p.sequences.length
                    ? p.sequences
                    : [{ workspace_id: "", days_until_send: "" }]
                  : [],
              }));
              setFormErrors((p) => {
                const next = { ...p };
                delete next.is_sequence_campaign;
                delete next.sequences;
                Object.keys(next).forEach((key) => {
                  if (key.startsWith("sequences.")) delete next[key];
                });
                return next;
              });
            }}
            onChange={(sequences) => {
              handleInputChange("sequences", sequences);
              setFormErrors((p) => {
                const next = { ...p };
                delete next.sequences;
                Object.keys(next).forEach((key) => {
                  if (key.startsWith("sequences.")) delete next[key];
                });
                return next;
              });
            }}
          />

          <div className="row mt-4">
            <div className="col-md-12 text-left">
              <button
                className="linear-gradient yr-submitbtn btn-sm"
                onClick={handleSubmit}
                disabled={saving}
                style={{ opacity: saving ? 0.75 : 1 }}
              >
                {saving
                  ? isEditing
                    ? "Updating..."
                    : "Saving..."
                  : isEditing
                    ? "Update"
                    : "Save"}
              </button>
            </div>
          </div>
        </div>
        <div className="col-md-5">
          {!isTriggered && (
            <CustomersTablePanelSMS
              customers={customersConfig.customers}
              disabled={saving}
              onNotify={(text) => {
                setMessage({ type: "success", text });
                setTimeout(() => setMessage(null), 7000);
              }}
              onCustomersChange={(updater) => {
                setCustomersConfig((prev) => {
                  const nextCustomers =
                    typeof updater === "function" ? updater(prev.customers || []) : updater;
                  return {
                    ...(prev || {}),
                    customers: Array.isArray(nextCustomers) ? nextCustomers : [],
                    contact_count: Array.isArray(nextCustomers) ? nextCustomers.length : 0,
                  };
                });
              }}
            />
          )}
        </div>
      </div>

      <ConfirmationModal
        open={showCancelConfirmModal}
        onClose={() => setShowCancelConfirmModal(false)}
        onConfirm={confirmDiscardChanges}
        title="Discard changes?"
        content="You have unsaved changes. Are you sure you want to go back to the list without saving?"
        cancelLabel="Stay"
        confirmLabel="Discard"
      />

      <ConfirmationModal
        open={showSendNowConfirmModal}
        onClose={() => setShowSendNowConfirmModal(false)}
        onConfirm={confirmSendNow}
        title="Send Now"
        content={`Send to up to ${customersConfig.customers?.length || 0} contacts now?`}
        cancelLabel="Cancel"
        confirmLabel="Send"
      />
    </div>
  );
};

export default CampaignSMSForm;
